import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import type { PlatformPermission } from '@/lib/permissions';
import { ServiceError } from '@/services';
import { useAuthStore } from '@/app/stores';
import { ButtonLink } from '@/components/ui/Button';
import { EmptyState, ErrorState, PageLoader } from '@/components/ui/States';
import { usePlatformSession } from './session';

function Denied({ description }: { description?: string }) {
  return (
    <EmptyState
      icon={ShieldAlert} title="Platform staff only"
      description={description ?? 'Only the platform team can open the Super Admin console. If you manage a single store, use its merchant admin instead.'}
      action={<div className="flex flex-wrap justify-center gap-2"><ButtonLink to="/admin" variant="outline">Merchant admin</ButtonLink><ButtonLink to="/" variant="ghost">Back to store</ButtonLink></div>}
      className="py-24"
    />
  );
}

/**
 * Layout-level gate for /super-admin. UX ONLY: a hidden page is not a protected resource. The real API must
 * independently verify the caller is platform staff on every request — see services/mock/platform/context.ts.
 * Tenant admins, staff and customers all fall through to `Denied` here, matching the requirement that only
 * a platform admin can reach this console at all.
 */
export function PlatformGate({ children }: { children: ReactNode }) {
  const { user, ready } = useAuthStore();
  const { actor, error, loading, load, reset } = usePlatformSession();
  const loc = useLocation();

  useEffect(() => {
    if (!ready) return;
    if (!user) { reset(); return; }
    void load();
  }, [user?.id, user?.role, ready, load, reset]);

  if (!ready) return <PageLoader label="Loading…" />;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  if (loading && !actor) return <PageLoader label="Loading the platform console…" />;
  if (error) {
    if (error instanceof ServiceError && error.code === 'FORBIDDEN') return <Denied />;
    if (error instanceof ServiceError && error.code === 'UNAUTHORIZED') return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
    return <ErrorState error={error} onRetry={() => void load()} />;
  }
  if (!actor) return <PageLoader />;
  return <>{children}</>;
}

/** Route-level permission guard, mirrors the tenant admin's AdminRoute. */
export function PlatformRoute({ permission, children }: { permission: PlatformPermission; children: ReactNode }) {
  const actor = usePlatformSession((s) => s.actor);
  if (!actor) return null;
  if (!actor.permissions.includes(permission)) return <Denied description="Your platform role does not include this section." />;
  return <>{children}</>;
}
